// §18.1 shared picker: CategoryPicker. A styled native <select> (keyboard/type-ahead
// searchable for free, matching AccountPicker's precedent) grouped by the §16.11 tree,
// plus an inline "+ Create" per PT-6 (creates immediately — no "provisional" concept exists
// yet for categories, unlike parties). `value`/`onChange` carry the category NAME, since
// transactions store `category` as free text (a fact captured at record time), not a live
// foreign key into the tree.
import { useState } from 'react';
import type { CategoryKind } from '@pfos/engine';
import { useCategories, useCreateCategory } from '../lib/categories';
import { ghostButtonInlineClass, inputClass, selectClass } from './FormKit';

const CREATE_SENTINEL = '__create__';

export function CategoryPicker({
  id,
  kind,
  value,
  onChange,
}: {
  id: string;
  kind: CategoryKind;
  value: string;
  onChange: (name: string) => void;
}): React.JSX.Element {
  const { data: categories } = useCategories({ kind });
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const createMutation = useCreateCategory();

  const roots = (categories ?? [])
    .filter((c) => c.parentId === undefined)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const childrenOf = (parentId: string): typeof roots =>
    (categories ?? [])
      .filter((c) => c.parentId === parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  if (creating) {
    return (
      <div className="flex gap-2">
        <input
          className={`${inputClass} flex-1`}
          aria-label="New category name"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          autoFocus
        />
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={createMutation.isPending}
          onClick={() => {
            const name = draft.trim();
            if (name === '') return;
            createMutation.mutate(
              { name, kind },
              {
                onSuccess: (created) => {
                  onChange(created.name);
                  setCreating(false);
                  setDraft('');
                },
              },
            );
          }}
        >
          Create
        </button>
      </div>
    );
  }

  return (
    <select
      id={id}
      className={selectClass}
      value={value}
      onChange={(e) => {
        if (e.target.value === CREATE_SENTINEL) {
          setCreating(true);
          return;
        }
        onChange(e.target.value);
      }}
    >
      <option value="">Select a category</option>
      {roots.map((root) => {
        const children = childrenOf(root.id);
        return (
          <optgroup key={root.id} label={root.name}>
            <option value={root.name}>{root.name}</option>
            {children.map((child) => (
              <option key={child.id} value={child.name}>
                {child.name}
              </option>
            ))}
          </optgroup>
        );
      })}
      <option value={CREATE_SENTINEL}>+ Create new…</option>
    </select>
  );
}
